/**
 * Libellés d'écran du « Face à face » (chantier C23 « Confrontation », SP2,
 * `VOCABULAIRE_ECRAN.md`) : le concept de l'auteur d'une note que je lis et le mien,
 * mis en regard. Les voisins reprennent les quatre intitulés de la fiche mobile
 * (« Employer plutôt », « Voir aussi », « Plus général », « Plus précis »). La phrase
 * d'échelle dit une proportion, jamais un jugement. L'écran ne dit jamais
 * « confrontation », « alignement », « comparer », « argumentation », « rang »,
 * « histogramme », « marque », « lisible », « divergence ». Testés contre `motsBannis`.
 */
import type { RaisonDeRapprochement, RelationVoisin } from '../../../types/api';
import { memeTerme } from '../../../utils/memeTerme';
import { CORRESPONDANCE_LABELS } from '../correspondances/correspondanceLabels';

/** La proportion en pourcentage entier ; une part non nulle ne s'arrondit jamais à « 0 % ». */
const pourcent = (part: number): number => {
  const borne = Math.max(0, Math.min(1, part));
  return borne > 0 ? Math.max(1, Math.round(borne * 100)) : 0;
};

export const FACE_A_FACE_LABELS = {
  /** Le bouton au pied du volet des définitions. */
  bouton: 'Face à face',
  /** Titre de la fenêtre et de l'écran ; deux clés quand je mets en face un concept d'un autre nom (même terme : casse et accents ignorés). */
  titre: (cle: string, maCle?: string): string =>
    (maCle && maCle.trim() && !memeTerme(cle, maCle)
      ? `Face à face : « ${cle} » et « ${maCle.trim()} »`
      : `Face à face : « ${cle} »`),
  /** Les deux côtés. */
  auteur: (prenom: string): string => prenom || "L'auteur de la note",
  vous: 'Vous',
  /** Rubriques. */
  ceQuEnDit: (prenom: string): string => `Ce qu'en dit ${prenom || "l'auteur de la note"}`,
  ceQueVousEnDites: 'Ce que vous en dites',
  voisins: 'Voisins',
  relations: {
    voir: 'Employer plutôt',
    voirAussi: 'Voir aussi',
    termesGeneriques: 'Plus général',
    termesSpecifiques: 'Plus précis',
  } as Record<RelationVoisin, string>,
  passages: 'Passages',
  /** La place d'une note d'importance dans l'échelle de la personne — une proportion, sans jugement. */
  auDessus: (part: number, prenom?: string): string =>
    (prenom === undefined
      ? `au-dessus de ${pourcent(part)} % de ce que vous avez noté`
      : `au-dessus de ${pourcent(part)} % de ce que ${prenom || "l'auteur de la note"} a noté`),
  aucunePlusBasse: (prenom?: string): string =>
    (prenom === undefined ? 'aucune note plus basse chez vous' : `aucune note plus basse chez ${prenom || "l'auteur de la note"}`),
  /** Nom accessible des étoiles d'un passage. */
  etoiles: (n: number): string => `${n} étoile${n > 1 ? 's' : ''} sur 5`,
  /** Puces de filtre. */
  tout: 'Tout',
  sourcesEnCommun: (n: number | string): string => `Sources en commun · ${n}`,
  voisinsEnCommun: (n: number | string): string => `Voisins en commun · ${n}`,
  memesPassages: (n: number | string): string => `Mêmes passages · ${n}`,
  filtres: 'Ce que vous avez en commun',
  /** Le sélecteur de mon côté. */
  autreConcept: 'Mettre en face un autre de mes concepts',
  aucunConcept: 'Aucun concept ne correspond.',
  /**
   * Pourquoi un concept répond à l'autre (C23 SP7) : la ligne du bloc « Face à face avec… » et la ligne
   * « Mis en face : … » sous mon concept. Une raison qui se lit, jamais un score.
   */
  raisons: {
    memeNom: 'même nom',
    selonVous: (relation: string): string => `${relation}, selon vous`,
    /** « autre forme « recueillement » chez vous », « … chez Claire », « … des deux côtés ». */
    autreForme: (forme: string, chez?: 'vous' | 'personne' | 'deux', prenom?: string): string =>
      (chez === 'personne'
        ? `autre forme « ${forme} » chez ${prenom || "l'auteur de la note"}`
        : chez === 'deux'
          ? `autre forme « ${forme} » des deux côtés`
          : `autre forme « ${forme} » chez vous`),
    /** Le moteur de repérage (C25) a reconnu mon concept dans un terme de l'autre. */
    reperage: (dans: string): string => `reconnu par le repérage dans « ${dans} »`,
  },
  /** Sous mon concept, quand ce n'est pas le même nom : pourquoi celui-là est en face. */
  misEnFace: (raison: string): string => `Mis en face : ${raison}`,
  /** États. */
  aucunPassage: 'Aucun passage pour l’instant.',
  aucunPassageFiltre: 'Aucun passage en commun de ce côté.',
  aucunVoisin: 'Aucun voisin.',
  /** La borne (50 notes, 50 passages) a laissé des passages de côté. */
  tronque: 'Tout n’est pas montré : les premiers passages seulement.',
  sansTitre: 'Note sans titre',
  chargement: 'Lecture du face à face…',
  indisponible: 'Face à face indisponible.',
  erreur: 'Impossible de lire ce face à face.',
  reessayer: 'Réessayer',
  /** Écran mobile. */
  retour: 'Retour',
  /** Nom accessible de l'écran. */
  region: 'Face à face',
  /** Les deux onglets de la fenêtre et de l'écran (C23 SP6). */
  onglets: { listes: 'Listes', carte: 'Carte' },
  /** La carte du face à face (C23 SP6) : foyers, passages, commun, étiquettes, légende. */
  carte: {
    region: 'Carte du face à face',
    memePassage: 'Même passage',
    memePassages: 'Mêmes passages',
    partage: 'Partagé',
    correspondance: 'Correspondance',
    /** La ligne de légende qui dit la taille des disques. */
    taille: 'Plus la note est importante pour la personne, plus le disque est grand.',
    /** Les notes de l'autre, hachurées. */
    notesDe: (prenom: string): string => `Notes de ${prenom || "l'auteur de la note"}`,
    /** L'étiquette du trait de correspondance : « Vous : correspond à peu près », « Claire : plus général que ». */
    etiquetteCorrespondance: (qui: string, relation: string): string => `${qui} : ${relation}`,
    /** Un voisin, avec sa relation en sous-titre (« plus général », « voir aussi »). */
    relationCourte: {
      voir: 'employer plutôt',
      voirAussi: 'voir aussi',
      termesGeneriques: 'plus général',
      termesSpecifiques: 'plus précis',
    } as Record<RelationVoisin, string>,
    voisinDesDeux: 'voisin des deux côtés',
    citeDesDeux: 'cité des deux côtés',
    /** Les gestes du téléphone. */
    touchez: 'Touchez un disque pour lire le passage ; pincez pour agrandir.',
    chargement: 'Lecture de la carte…',
  },
  /** La fiche du nœud touché sur la carte (C23 SP6). */
  fiche: {
    votreNote: 'votre note',
    noteDe: (prenom: string): string => `note de ${prenom || "l'auteur de la note"}`,
    votreConcept: 'votre concept',
    conceptDe: (prenom: string): string => `concept de ${prenom || "l'auteur de la note"}`,
    memePassageChez: (prenom: string): string => `Même passage chez ${prenom || "l'auteur de la note"}`,
    memePassageChezVous: 'Même passage chez vous',
    liens: 'Liens',
    citePar: 'Cité par',
    voisins: 'Voisins',
    chezVous: 'chez vous',
    chez: (prenom: string): string => `chez ${prenom || "l'auteur de la note"}`,
    ouvrirNote: 'Ouvrir la note',
    voirDocument: 'Voir dans le document',
    passages: (n: number): string => `${n} passage${n > 1 ? 's' : ''}`,
    document: 'Document',
    /** Un document de l'autre : son nom et son format, sans bouton (aucun fichier à moi). */
    formatDe: (type: string): string => (type ? type.toUpperCase() : ''),
    rien: 'Touchez ou cliquez un disque pour lire ce qu’il porte.',
  },
} as const;

/** La raison en face : « correspondance » ou « même nom » ou « autre forme », ou le repérage du client (C23 SP7). */
export type RaisonEnFace = RaisonDeRapprochement | { sorte: 'reperage'; dans: string };

/** La ligne de l'écran pour une raison ; `prenom` nomme l'autre quand la forme est chez elle. */
export function texteDeRaison(raison: RaisonEnFace, prenom?: string): string {
  const R = FACE_A_FACE_LABELS.raisons;
  switch (raison.sorte) {
    case 'correspondance': return R.selonVous(CORRESPONDANCE_LABELS.relations[raison.relation].toLowerCase());
    case 'memeNom': return R.memeNom;
    case 'autreForme': return R.autreForme(raison.forme, raison.chez, prenom);
    case 'reperage': return R.reperage(raison.dans);
    default: return R.memeNom;
  }
}
